import type { AppApi, EDITOR_TYPE } from '@wix/wix-data-client-common'
import type { ComponentRef, PageRef } from '@wix/editor-platform-sdk-types'
import type { EditorSDK } from '@wix/wix-data-client-common-standalone'
import type { FieldSpec, Schema } from '@wix/wix-data-schema-types'
import type { Filter } from '@wix/wix-data-client-types'
import {
  createCategoryItemFieldMap,
  createCategoryRouterFieldMap,
  createRepeaterNavMenuFieldMap,
} from '../../presets/categoryPresetFieldMap'
import { getCollectionFields } from '../utils/getCollectionFields'
import { getCategoryPageDatasets } from './getCategoryPageDatasets'
import { capitalize } from 'lodash'

export type MultiReferenceField = {
  referencedCollectionId: string
  referencingFieldKey: string
}

const updateDatasetLinkFilters = (
  filters: Filter[],
  firstCategoryConsumerField: MultiReferenceField | undefined,
): Filter[] => {
  if (!firstCategoryConsumerField) {
    return filters.filter(filter => !filter.dataset)
  }
  return filters.map(filter => {
    if (!filter.dataset) {
      return filter
    }
    return { ...filter, field: firstCategoryConsumerField.referencingFieldKey }
  })
}

const toDefinedFieldMap = (
  fieldMap: Record<string, string | undefined>,
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(fieldMap).filter(
      (entry): entry is [string, string] => entry[1] !== undefined,
    ),
  )

export const rebindToCollection = (
  appApi: AppApi,
  {
    controllerRef,
    fieldMap,
    newCollectionId,
  }: {
    controllerRef: ComponentRef
    fieldMap: Record<string, string | undefined>
    newCollectionId: string
  },
) =>
  appApi.rebindComponentsToAnotherCollection({
    fieldMap: toDefinedFieldMap(fieldMap),
    controllerRef,
    newCollectionId,
    respectMainProperty: true,
  })

const getSchemaName = (schema: Schema) => capitalize(schema.displayName)

const rebindRouterDataset = async ({
  appApi,
  routerDatasetRef,
  schema,
  categoryFields,
  editorType,
}: {
  appApi: AppApi
  routerDatasetRef: ComponentRef
  schema: Schema
  categoryFields: { [x: string]: FieldSpec }
  editorType: EDITOR_TYPE
}) => {
  try {
    const routerFieldMap = createCategoryRouterFieldMap({
      editorType,
      fields: categoryFields,
    })
    await appApi.setDatasetDisplayName(
      routerDatasetRef,
      `${getSchemaName(schema)} Category Item`,
      true,
    )
    await rebindToCollection(appApi, {
      controllerRef: routerDatasetRef,
      fieldMap: routerFieldMap,
      newCollectionId: schema.id,
    })
  } catch (error: unknown) {
    throw new Error('Failed to rebind router dataset', { cause: error })
  }
}

const rebindRepeaterMenuDataset = async ({
  appApi,
  repeaterNavMenuDatasetRef,
  schema,
  categoryFields,
  pageLinkFieldKey,
  editorType,
}: {
  appApi: AppApi
  repeaterNavMenuDatasetRef: ComponentRef
  schema: Schema
  categoryFields: {
    [x: string]: FieldSpec
  }
  pageLinkFieldKey: string
  editorType: EDITOR_TYPE
}) => {
  try {
    // Bind navigation dataset → category collection
    await appApi.changePresetDatasetCollection({
      controllerRef: repeaterNavMenuDatasetRef,
      collectionId: schema.id,
      collectionName: schema.displayName,
    })
    await appApi.setDatasetDisplayName(
      repeaterNavMenuDatasetRef,
      `${getSchemaName(schema)} Menu`,
      true,
    )
    await rebindToCollection(appApi, {
      controllerRef: repeaterNavMenuDatasetRef,
      fieldMap: createRepeaterNavMenuFieldMap({
        editorType,
        fields: categoryFields,
        pageLinkFieldKey,
      }),
      newCollectionId: schema.id,
    })
  } catch (error: unknown) {
    throw new Error('Failed to rebind repeater nav menu dataset', {
      cause: error,
    })
  }
}

const rebindRegularDataset = async ({
  appApi,
  regularDatasetRef,
  schema,
  referencedSchemas,
  categoryConsumerFields,
  editorType,
}: {
  appApi: AppApi
  regularDatasetRef: ComponentRef
  schema: Schema
  referencedSchemas: Schema[]
  categoryConsumerFields: MultiReferenceField[]
  editorType: EDITOR_TYPE
}) => {
  try {
    const firstCategoryConsumerField = categoryConsumerFields[0]
    const firstReferencedSchema = referencedSchemas[0]
    const targetSchema = firstReferencedSchema
    const targetCollectionId = firstReferencedSchema?.id ?? schema.id

    if (!targetSchema) {
      return
    }

    const { formattedFields: targetFields } = getCollectionFields({
      schema: targetSchema,
      isFromExistingCollection: true,
    })
    const regularFieldMap = createCategoryItemFieldMap({
      editorType,
      fields: targetFields,
    })

    await rebindToCollection(appApi, {
      controllerRef: regularDatasetRef,
      fieldMap: regularFieldMap,
      newCollectionId: targetCollectionId,
    })
    await appApi.changePresetDatasetCollection({
      controllerRef: regularDatasetRef,
      collectionId: targetCollectionId,
      collectionName: targetSchema.displayName,
    })
    await appApi.setDatasetDisplayName(
      regularDatasetRef,
      `${getSchemaName(targetSchema)} List`,
      true,
    )
    const datasetInfo = await appApi.fetchDataset(regularDatasetRef)
    const updatedFilters = updateDatasetLinkFilters(
      datasetInfo.config.filters,
      firstCategoryConsumerField,
    )
    await appApi.updateDatasetConfig(regularDatasetRef, {
      ...datasetInfo.config,
      filters: updatedFilters,
    })
  } catch (error: unknown) {
    throw new Error('Failed to rebind regular dataset', { cause: error })
  }
}

export const bindCategoryDatasets = async ({
  appApi,
  editorSdkProxy,
  pageRef,
  schema,
  categoryConsumerFields,
  referencedSchemas,
  presetCategoryCollectionName,
  pageLinkFieldKey,
  editorType,
}: {
  appApi: AppApi
  editorSdkProxy: EditorSDK
  pageRef: PageRef
  schema: Schema
  categoryConsumerFields: MultiReferenceField[]
  referencedSchemas: Schema[]
  presetCategoryCollectionName: string
  pageLinkFieldKey: string
  editorType: EDITOR_TYPE
}): Promise<ComponentRef | undefined> => {
  const { routerDatasetRef, regularDatasetRef, repeaterNavMenuDatasetRef } =
    await getCategoryPageDatasets({
      editorSdkProxy,
      pageRef,
      presetCategoryCollectionName,
    })

  const { formattedFields: categoryFields } = getCollectionFields({
    schema,
    isFromExistingCollection: true,
  })

  const hasCategoryConsumerFields = categoryConsumerFields.length > 0

  if (!hasCategoryConsumerFields) {
    await editorSdkProxy.components.remove({
      componentRef: regularDatasetRef,
    })
  }

  await Promise.all([
    rebindRouterDataset({
      appApi,
      routerDatasetRef,
      schema,
      categoryFields,
      editorType,
    }),
    rebindRepeaterMenuDataset({
      appApi,
      repeaterNavMenuDatasetRef,
      schema,
      categoryFields,
      pageLinkFieldKey,
      editorType,
    }),
    ...(hasCategoryConsumerFields
      ? [
          rebindRegularDataset({
            appApi,
            regularDatasetRef,
            schema,
            referencedSchemas,
            categoryConsumerFields,
            editorType,
          }),
        ]
      : []),
  ])

  return regularDatasetRef
}
